import React from "react";
import { cn } from "@/lib/utils";
import { MapPin, Check, Camera, Image as ImageIcon, AlertTriangle, Send } from "lucide-react";

function WorkerItem({ item, index, onToggle, onTakeProof, onOpenPhoto }) {
  const proof = item.proof;
  const isDone = item.status === "done" || item.status === "sub_done";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border-2 shadow-lg transition-all duration-200",
        isDone ? "border-emerald-500/40 bg-emerald-500/10" : "border-neutral-800 bg-[#141414] shadow-black/40"
      )}
      data-testid={`demo-worker-item-${item.id}`}
    >
      <div className="relative">
        <button onClick={() => onOpenPhoto(item.ref)} className="block w-full">
          <img src={item.ref} alt="" className={cn("max-h-[42vh] w-full bg-neutral-900 object-cover transition-opacity", isDone ? "opacity-60" : "opacity-100")} style={{ aspectRatio: "4 / 3" }} />
        </button>
        {isDone && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="rotate-[-8deg] rounded-sm border-4 border-emerald-400/90 bg-black/40 px-4 py-1 backdrop-blur-sm">
              <span className="text-xl font-black tracking-[0.3em] text-emerald-300">COMPLETE</span>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        data-testid={`demo-worker-complete-${item.id}`}
        onClick={() => onToggle(item)}
        className="flex min-h-[76px] w-full items-start gap-4 p-5 text-left transition-colors active:bg-white/[0.04]"
      >
        <span className={cn("mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border-[2.5px] transition-colors", isDone ? "border-emerald-500 bg-emerald-500" : "border-neutral-600 bg-neutral-900")}>
          {isDone && <Check className="h-6 w-6 text-black" strokeWidth={3.5} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-400/80">Item {index + 1}</span>
          <span className={cn("mt-0.5 block text-[17px] font-medium leading-[1.35]", isDone ? "text-neutral-500 line-through" : "text-neutral-50")}>{item.title}</span>
        </span>
      </button>

      <div className="space-y-3 px-5 pb-5">
        {proof && (
          <div className="grid grid-cols-4 gap-2">
            <button onClick={() => onOpenPhoto(proof.url)} className="aspect-square overflow-hidden rounded-lg border border-emerald-500/50" data-testid={`demo-worker-proof-${item.id}`}>
              <img src={proof.url} alt="" className="h-full w-full object-cover" />
            </button>
          </div>
        )}
        <div className="space-y-2 pt-0.5">
          {!proof && (
            <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5" /> Photo needed
            </div>
          )}
          <button
            data-testid={`demo-worker-take-proof-${item.id}`}
            onClick={() => onTakeProof(item)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 text-[15px] font-bold text-black shadow-lg shadow-emerald-500/25 transition-transform active:scale-[0.98]"
          >
            <Camera className="h-5 w-5" strokeWidth={2.5} /> {proof ? "Add another" : "Take proof"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DemoWorker({ job, items, onToggle, onTakeProof, onOpenPhoto, onSend }) {
  const total = items.length;
  const doneCount = items.filter((it) => it.proof || ["done", "sub_done"].includes(it.status)).length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="min-h-[100dvh] overflow-x-hidden bg-[#0a0a0a] text-white" style={{ paddingBottom: "128px" }} data-testid="demo-worker">
      <header className="border-b border-neutral-800 bg-[#0a0a0a]/95 backdrop-blur-md">
        <div className="mx-auto max-w-2xl px-4 pb-4 pt-4">
          <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-emerald-400">Punch List</div>
          <h1 className="mt-1 font-heading text-[26px] font-bold leading-tight tracking-tight sm:text-[30px]">{job.name}</h1>
          <div className="mt-1 flex items-center gap-1.5 truncate text-sm text-neutral-400">
            <span className="truncate">{job.name}</span>
            <span>·</span><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{job.location}</span>
          </div>
          <div className="mt-3">
            <div className="mb-1.5 flex items-center justify-between text-[13px]">
              <span className="text-neutral-400">Progress</span>
              <span className="font-bold text-emerald-400" data-testid="demo-worker-progress">{doneCount}/{total} · {pct}%</span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-neutral-800">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto mt-4 max-w-2xl px-4">
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
          <Camera className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" strokeWidth={2.5} />
          <p className="min-w-0 flex-1 text-[14px] font-medium leading-snug text-emerald-100">Snap a proof photo on each item, mark it done, then send it back.</p>
        </div>
      </div>

      <div className="mx-auto max-w-2xl space-y-4 px-4 pt-4">
        {items.map((it, i) => (
          <WorkerItem key={it.id} index={i} item={it} onToggle={onToggle} onTakeProof={onTakeProof} onOpenPhoto={onOpenPhoto} />
        ))}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-neutral-800 bg-[#0a0a0a]/95 pb-[calc(env(safe-area-inset-bottom)+14px)] pt-3.5 backdrop-blur-lg">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4">
          <div className="min-w-0">
            <div className="text-[15px] font-bold">{doneCount} of {total} done</div>
            <div className="truncate text-[12px] text-neutral-500">The sender gets notified the moment you send</div>
          </div>
          <button
            data-testid="demo-worker-send"
            onClick={onSend}
            className="shrink-0 rounded-full bg-emerald-500 px-6 py-3.5 text-[15px] font-bold text-black shadow-lg shadow-emerald-500/30 active:scale-95"
          >
            Send proof back
          </button>
        </div>
      </div>
    </div>
  );
}
